import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ListView } from '../../design-system/list-view/ListView'
import { RowActionsMenu } from '../../design-system/list-view/RowActionsMenu'
import type { ActiveFilterChip } from '../../design-system/list-view/FilterPanel'
import type { Density, ExportFormat, SortSpec } from '../../design-system/list-view/types'
import { usePersistentState } from '../../lib/usePersistentState'
import { useDebouncedValue } from '../../lib/useDebouncedValue'
import { useSavedViews } from '../../lib/savedViews'
import { rsqlAnd, rsqlEquals, rsqlIn, rsqlSearch } from '../../lib/rsql'
import { runExport, exportCapNote, type ExportColumn } from '../../lib/export'
import { listAccounts } from '../../api/accounts'
import { ApiError } from '../../api/problem'
import { ACCOUNT_STATUSES, ACCOUNT_TYPES, type AccountStatus, type AccountType, type AccountView } from '../../types/account'
import { ACCOUNT_COLUMN_ORDER, ACCOUNT_STATUS_LABEL, ACCOUNT_TYPE_LABEL, buildAccountColumns } from './columns'
import { AccountFormModal } from './AccountFormModal'

const PAGE_LIMIT = 50
const REGIONS = ['Americas', 'EMEA', 'APAC']
const CASE_TOOLTIP = 'Arrives with the M6 Case module.'
const FISCAL_YEAR_TOOLTIP = 'Arrives with the M6 Case module (fiscal year is set at the case level).'

interface FacetState {
  types: Set<AccountType>
  statuses: Set<AccountStatus>
  countries: string[]
  regions: Set<string>
  ultimateParentId: string
}

function emptyFacets(): FacetState {
  return { types: new Set(), statuses: new Set(), countries: [], regions: new Set(), ultimateParentId: '' }
}

/** JSON-serializable projection of `FacetState` (Sets aren't valid JSON) — the shape a saved view
 * actually persists; see `serializeFacets`/`deserializeFacets`. */
interface SerializedFacetState {
  types: AccountType[]
  statuses: AccountStatus[]
  countries: string[]
  regions: string[]
  ultimateParentId: string
}

function serializeFacets(facets: FacetState): SerializedFacetState {
  return {
    types: [...facets.types],
    statuses: [...facets.statuses],
    countries: facets.countries,
    regions: [...facets.regions],
    ultimateParentId: facets.ultimateParentId,
  }
}

function deserializeFacets(facets: SerializedFacetState): FacetState {
  return {
    types: new Set(facets.types),
    statuses: new Set(facets.statuses),
    countries: facets.countries,
    regions: new Set(facets.regions),
    ultimateParentId: facets.ultimateParentId,
  }
}

/** Everything a saved view needs to capture for this list — filters (facets + search), sort, and
 * columns/density (Saved Views/Filters story; `lib/savedViews.ts`). */
interface AccountsSavedViewState {
  facets: SerializedFacetState
  search: string
  sort: SortSpec[]
  columnOrder: string[]
  hiddenColumnIds: string[]
  pinnedColumnIds: string[]
  density: Density
}

function buildFilter(facets: FacetState, search: string): string | null {
  const typeClause = rsqlIn('type', [...facets.types])
  const statusClause = rsqlIn('status', [...facets.statuses])
  const countryClause = rsqlIn('country', facets.countries)
  const regionClause = rsqlIn('region', [...facets.regions])
  const ultimateParentClause = facets.ultimateParentId.trim()
    ? rsqlEquals('ultimateParentId', facets.ultimateParentId.trim())
    : null
  const searchClause = rsqlSearch(['name', 'country', 'industry'], search)
  return rsqlAnd(typeClause, statusClause, countryClause, regionClause, ultimateParentClause, searchClause) || null
}

function buildSort(sort: SortSpec[]): string | null {
  if (sort.length === 0) return null
  return sort.map((s) => (s.direction === 'desc' ? `-${s.columnId}` : s.columnId)).join(',')
}

export function AccountsListScreen() {
  const navigate = useNavigate()
  const columns = useMemo(() => buildAccountColumns(), [])

  // --- persisted view state (per-user, localStorage — see usePersistentState) ---
  const [columnOrder, setColumnOrder] = usePersistentState<string[]>('iw.accounts.columnOrder', ACCOUNT_COLUMN_ORDER)
  const [hiddenColumnIdsArr, setHiddenColumnIdsArr] = usePersistentState<string[]>(
    'iw.accounts.hiddenColumns',
    columns.filter((c) => c.defaultHidden).map((c) => c.id),
  )
  const [pinnedColumnIdsArr, setPinnedColumnIdsArr] = usePersistentState<string[]>('iw.accounts.pinnedColumns', ['name'])
  const [density, setDensity] = usePersistentState<Density>('iw.accounts.density', 'compact')

  const hiddenColumnIds = useMemo(() => new Set(hiddenColumnIdsArr), [hiddenColumnIdsArr])
  const pinnedColumnIds = useMemo(() => new Set(pinnedColumnIdsArr), [pinnedColumnIdsArr])

  // --- controls state ---
  const [facets, setFacets] = useState<FacetState>(emptyFacets)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const [sort, setSort] = useState<SortSpec[]>([{ columnId: 'name', direction: 'asc' }])
  const [selection, setSelection] = useState<Set<string>>(new Set())
  const [countryInput, setCountryInput] = useState('')
  const [ultimateParentInput, setUltimateParentInput] = useState('')

  // --- create/edit modal ---
  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; account: AccountView } | null>(null)

  // --- data ---
  const [accounts, setAccounts] = useState<AccountView[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filterString = useMemo(() => buildFilter(facets, debouncedSearch), [facets, debouncedSearch])
  const sortString = useMemo(() => buildSort(sort), [sort])
  // Guards against out-of-order responses (e.g. a slow first keystroke's
  // request resolving after a faster later one) clobbering fresher data —
  // a real race, not a theoretical one, once search is debounced server-side.
  const latestRequestRef = useRef(0)

  const fetchPage = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      const requestId = ++latestRequestRef.current
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        } else {
          setLoadingMore(true)
        }
        const page = await listAccounts({ filter: filterString, sort: sortString, cursor: afterCursor, limit: PAGE_LIMIT })
        if (latestRequestRef.current !== requestId) return
        setAccounts((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not load accounts. Check your connection and try again.')
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [filterString, sortString],
  )

  useEffect(() => {
    setSelection(new Set())
    void fetchPage(null, true)
    // Re-fetch page 1 whenever the query (filter/sort) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString, sortString])

  function retry() {
    void fetchPage(null, true)
  }

  // --- quick filters (derived from + mutate facet state directly) ---
  const quickFilters = useMemo(
    () => [
      { id: 'all', label: 'All' },
      { id: 'active', label: 'Active' },
      ...REGIONS.map((region) => ({ id: `region:${region}`, label: region })),
    ],
    [],
  )
  const activeQuickFilterIds = useMemo(() => {
    const ids = new Set<string>()
    if (facets.statuses.size === 0 && facets.regions.size === 0) ids.add('all')
    if (facets.statuses.has('ACTIVE')) ids.add('active')
    for (const region of facets.regions) ids.add(`region:${region}`)
    return ids
  }, [facets])

  function onQuickFilterToggle(id: string) {
    if (id === 'all') {
      setFacets(emptyFacets())
      setCountryInput('')
      setUltimateParentInput('')
      return
    }
    if (id === 'active') {
      setFacets((prev) => {
        const statuses = new Set(prev.statuses)
        if (statuses.has('ACTIVE')) statuses.delete('ACTIVE')
        else statuses.add('ACTIVE')
        return { ...prev, statuses }
      })
      return
    }
    if (id.startsWith('region:')) {
      const region = id.slice('region:'.length)
      setFacets((prev) => {
        const regions = new Set(prev.regions)
        if (regions.has(region)) regions.delete(region)
        else regions.add(region)
        return { ...prev, regions }
      })
    }
  }

  // --- advanced filter chips summary ---
  const activeFilterChips: ActiveFilterChip[] = useMemo(() => {
    const chips: ActiveFilterChip[] = []
    for (const type of facets.types) chips.push({ id: `type:${type}`, label: `Type: ${ACCOUNT_TYPE_LABEL[type]}` })
    for (const status of facets.statuses) chips.push({ id: `status:${status}`, label: `Status: ${ACCOUNT_STATUS_LABEL[status]}` })
    for (const country of facets.countries) chips.push({ id: `country:${country}`, label: `Country: ${country}` })
    for (const region of facets.regions) chips.push({ id: `region:${region}`, label: `Region: ${region}` })
    if (facets.ultimateParentId) chips.push({ id: 'ultimateParentId', label: `Ultimate parent: ${facets.ultimateParentId}` })
    return chips
  }, [facets])

  function removeFilterChip(id: string) {
    if (id === 'ultimateParentId') {
      setFacets((prev) => ({ ...prev, ultimateParentId: '' }))
      setUltimateParentInput('')
      return
    }
    const [kind, value] = id.split(':', 2) as [string, string]
    setFacets((prev) => {
      const next = { ...prev }
      if (kind === 'type') next.types = new Set([...prev.types].filter((t) => t !== value))
      if (kind === 'status') next.statuses = new Set([...prev.statuses].filter((s) => s !== value))
      if (kind === 'country') next.countries = prev.countries.filter((c) => c !== value)
      if (kind === 'region') next.regions = new Set([...prev.regions].filter((r) => r !== value))
      return next
    })
  }

  function clearAllFilters() {
    setFacets(emptyFacets())
    setCountryInput('')
    setUltimateParentInput('')
    setSearch('')
  }

  const hasAnyFilterOrSearch =
    facets.types.size > 0 ||
    facets.statuses.size > 0 ||
    facets.countries.length > 0 ||
    facets.regions.size > 0 ||
    Boolean(facets.ultimateParentId) ||
    debouncedSearch.trim() !== ''

  // --- column manager handlers ---
  function toggleColumnHidden(id: string) {
    setHiddenColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  function moveColumn(id: string, direction: -1 | 1) {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const tmp = next[index]!
      next[index] = next[target]!
      next[target] = tmp
      return next
    })
  }
  function toggleColumnPin(id: string) {
    setPinnedColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  // --- saved views (browser-local "personal" scope — see lib/savedViews.ts) ---
  const savedViewState: AccountsSavedViewState = useMemo(
    () => ({
      facets: serializeFacets(facets),
      search,
      sort,
      columnOrder,
      hiddenColumnIds: hiddenColumnIdsArr,
      pinnedColumnIds: pinnedColumnIdsArr,
      density,
    }),
    [facets, search, sort, columnOrder, hiddenColumnIdsArr, pinnedColumnIdsArr, density],
  )
  const applySavedViewState = useCallback((state: AccountsSavedViewState) => {
    setFacets(deserializeFacets(state.facets))
    setCountryInput(state.facets.countries.join(', '))
    setUltimateParentInput(state.facets.ultimateParentId)
    setSearch(state.search)
    setSort(state.sort)
    setColumnOrder(state.columnOrder)
    setHiddenColumnIdsArr(state.hiddenColumnIds)
    setPinnedColumnIdsArr(state.pinnedColumnIds)
    setDensity(state.density)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const savedViews = useSavedViews<AccountsSavedViewState>('accounts', savedViewState, applySavedViewState)

  // --- export ---
  const exportColumns: ExportColumn<AccountView>[] = useMemo(
    () =>
      columnOrder
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined && !hiddenColumnIds.has(c.id))
        .map((c) => ({ id: c.id, header: c.header, toText: c.toExportText })),
    [columnOrder, hiddenColumnIds, columns],
  )

  function handleExport(format: ExportFormat) {
    runExport(format, 'Accounts', accounts, exportColumns)
  }
  function handleExportSelected(selectedIds: string[]) {
    const idSet = new Set(selectedIds)
    runExport('csv', 'Accounts (selected)', accounts.filter((a) => idSet.has(a.id)), exportColumns)
  }

  // --- derived state for ListView ---
  const state: 'loading' | 'error' | 'empty-tenant' | 'empty-filtered' | 'ready' = error
    ? 'error'
    : loading
      ? 'loading'
      : accounts.length === 0
        ? hasAnyFilterOrSearch
          ? 'empty-filtered'
          : 'empty-tenant'
        : 'ready'

  return (
    <>
      <ListView<AccountView>
        entityIcon="building2"
        title="Accounts"
        helpId="list.clients"
        countLabel={loading ? undefined : `${accounts.length}${cursor ? '+' : ''}`}
        primaryAction={{ label: 'New account', icon: 'plus', onClick: () => setModal({ mode: 'create' }) }}
        quickFilters={quickFilters}
        activeQuickFilterIds={activeQuickFilterIds}
        onQuickFilterToggle={onQuickFilterToggle}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search accounts"
        searchPlaceholder="Search by name, country, industry…"
        filterChildren={
          <FacetFields
            facets={facets}
            setFacets={setFacets}
            countryInput={countryInput}
            setCountryInput={setCountryInput}
            ultimateParentInput={ultimateParentInput}
            setUltimateParentInput={setUltimateParentInput}
          />
        }
        activeFilterCount={activeFilterChips.length}
        activeFilterChips={activeFilterChips}
        onRemoveFilterChip={removeFilterChip}
        onClearFilters={clearAllFilters}
        savedViews={savedViews}
        onExport={handleExport}
        exportNote={exportCapNote(accounts.length, Boolean(cursor))}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumnIds={hiddenColumnIds}
        pinnedColumnIds={pinnedColumnIds}
        onToggleColumnHidden={toggleColumnHidden}
        onMoveColumn={moveColumn}
        onToggleColumnPin={toggleColumnPin}
        sort={sort}
        onSortChange={setSort}
        density={density}
        onDensityChange={setDensity}
        state={state}
        rows={accounts}
        getRowId={(row) => row.id}
        onRowActivate={(row) => navigate(`/clients/accounts/${row.id}`)}
        renderRowActions={(row) => (
          <RowActionsMenu
            label={`Actions for ${row.name}`}
            actions={[
              { id: 'open', label: 'Open', icon: 'chevron-right', onSelect: () => navigate(`/clients/accounts/${row.id}`) },
              {
                id: 'new-legal-entity',
                label: 'New legal entity here',
                icon: 'landmark',
                onSelect: () => navigate(`/clients/legal-entities/new?accountId=${row.id}`),
              },
              { id: 'edit', label: 'Edit', icon: 'settings', onSelect: () => setModal({ mode: 'edit', account: row }) },
            ]}
          />
        )}
        selection={selection}
        onSelectionChange={setSelection}
        bulkActions={[{ id: 'export-selected', label: 'Export selected', icon: 'download', onRun: handleExportSelected }]}
        errorMessage={error ?? undefined}
        onRetry={retry}
        emptyTenant={{
          icon: 'building2',
          title: 'No accounts yet',
          description: 'Create the first account for this tenant to get started.',
          action: (
            <button type="button" className="iw-btn iw-btn--primary" onClick={() => setModal({ mode: 'create' })}>
              New account
            </button>
          ),
        }}
        hasMore={Boolean(cursor)}
        onLoadMore={() => void fetchPage(cursor, false)}
        loadingMore={loadingMore}
      />

      {modal?.mode === 'create' ? (
        <AccountFormModal
          mode="create"
          onClose={() => setModal(null)}
          onSaved={(account) => {
            setModal(null)
            navigate(`/clients/accounts/${account.id}`)
          }}
        />
      ) : null}
      {modal?.mode === 'edit' ? (
        <AccountFormModal
          mode="edit"
          editing={{ account: modal.account, version: modal.account.version }}
          onClose={() => setModal(null)}
          onSaved={(account) => {
            setModal(null)
            setAccounts((prev) => prev.map((a) => (a.id === account.id ? account : a)))
          }}
        />
      ) : null}
    </>
  )
}

interface FacetFieldsProps {
  facets: FacetState
  setFacets: (updater: (prev: FacetState) => FacetState) => void
  countryInput: string
  setCountryInput: (value: string) => void
  ultimateParentInput: string
  setUltimateParentInput: (value: string) => void
}

/** The Type / Status / Country / Region / Ultimate parent facets inside the FilterPanel. */
function FacetFields({ facets, setFacets, countryInput, setCountryInput, ultimateParentInput, setUltimateParentInput }: FacetFieldsProps) {
  function toggleSetMember<T>(set: Set<T>, value: T): Set<T> {
    const next = new Set(set)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    return next
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className="iw-field-label" style={{ marginBottom: 4 }}>
          Type
        </legend>
        {ACCOUNT_TYPES.map((type) => (
          <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--iw-size-sm)', padding: '2px 0' }}>
            <input
              type="checkbox"
              checked={facets.types.has(type)}
              onChange={() => setFacets((prev) => ({ ...prev, types: toggleSetMember(prev.types, type) }))}
            />
            {ACCOUNT_TYPE_LABEL[type]}
          </label>
        ))}
      </fieldset>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className="iw-field-label" style={{ marginBottom: 4 }}>
          Status
        </legend>
        {ACCOUNT_STATUSES.map((status) => (
          <label key={status} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--iw-size-sm)', padding: '2px 0' }}>
            <input
              type="checkbox"
              checked={facets.statuses.has(status)}
              onChange={() => setFacets((prev) => ({ ...prev, statuses: toggleSetMember(prev.statuses, status) }))}
            />
            {ACCOUNT_STATUS_LABEL[status]}
          </label>
        ))}
      </fieldset>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className="iw-field-label" style={{ marginBottom: 4 }}>
          Region
        </legend>
        {REGIONS.map((region) => (
          <label key={region} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--iw-size-sm)', padding: '2px 0' }}>
            <input
              type="checkbox"
              checked={facets.regions.has(region)}
              onChange={() => setFacets((prev) => ({ ...prev, regions: toggleSetMember(prev.regions, region) }))}
            />
            {region}
          </label>
        ))}
      </fieldset>

      <div className="iw-field">
        <label className="iw-field-label" htmlFor="facet-country">
          Country (comma-separated)
        </label>
        <input
          id="facet-country"
          className="iw-input"
          value={countryInput}
          onChange={(e) => setCountryInput(e.target.value)}
          onBlur={() =>
            setFacets((prev) => ({
              ...prev,
              countries: countryInput
                .split(',')
                .map((c) => c.trim())
                .filter(Boolean),
            }))
          }
          placeholder="e.g. United States, United Kingdom"
        />
      </div>

      <div className="iw-field">
        <label className="iw-field-label" htmlFor="facet-ultimate-parent">
          Ultimate parent ID
        </label>
        <input
          id="facet-ultimate-parent"
          className="iw-input"
          value={ultimateParentInput}
          onChange={(e) => setUltimateParentInput(e.target.value)}
          onBlur={() => setFacets((prev) => ({ ...prev, ultimateParentId: ultimateParentInput.trim() }))}
          placeholder="UUID — pending a name-based lookup"
        />
        <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)' }}>
          Known gap: no name-based ultimate-parent facet yet — see UX report.
        </span>
      </div>

      <div style={{ opacity: 0.5 }} title={CASE_TOOLTIP}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--iw-size-sm)' }}>
          <input type="checkbox" disabled /> Case <span className="iw-pill iw-pill--muted">Not available</span>
        </label>
      </div>
      <div style={{ opacity: 0.5 }} title={FISCAL_YEAR_TOOLTIP}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--iw-size-sm)' }}>
          <input type="checkbox" disabled /> Fiscal year <span className="iw-pill iw-pill--muted">Not available</span>
        </label>
      </div>
    </div>
  )
}
